import { osName } from 'react-device-detect';
import Cookies from 'js-cookie';
import * as types from 'store/actions/types';
import services from 'services/services';
import { setChannelsByLocal } from 'store/actions';

// Import helpers
import { LOCALSTORAGE_USER, COOKIE_TOKEN } from 'helpers/variables';
import { checkUserResources, checkUserPackets } from './auth_helpers';
import { getErrorCodeResults, getCodeErrorMessage, API_ERRORS } from 'helpers/error_code_api';
import { sendLoginEvent } from 'helpers/tracking';

// Import api
import * as API from 'helpers/api';

// ******************** AUTHORIZE OPERATORS ***********************

export const userAuthSuccess =
	({ user, isExternalLogin, providerId }) =>
	async (dispatch) => {
		const userData = checkUserResources(user);
		localStorage.setItem(LOCALSTORAGE_USER, JSON.stringify(userData));
		dispatch({
			type: types.AUTH_USER_SUCCESS,
			payload: userData
		});

		sendLoginEvent(isExternalLogin, providerId);
	};

export const userAuthError = (error) => (dispatch) => {
	const { codeErrorMessage } = getErrorCodeResults(error);
	error.response.data.message = codeErrorMessage;
	dispatch({
		type: types.AUTH_USER_ERROR,
		payload: error.response
	});
};

export const userAuthMultipleAccounts = (data) => (dispatch) => {
	Cookies.set(COOKIE_TOKEN, data.token);

	dispatch({
		type: types.AUTH_USER_MULTIPLE_ACCOUNT,
		payload: data
	});
};

export const clearAuthMultipleAccounts = () => (dispatch) => {
	Cookies.remove(COOKIE_TOKEN);

	dispatch({
		type: types.CLEAR_AUTH_USER_MULTIPLE_ACCOUNT,
		payload: true
	});
};

export const userLinkAccount = () => (dispatch) => {
	dispatch({
		type: types.NOT_RELATED_ACCOUNT_ERROR
	});
};

export const saveProviderToken =
	({ idToken, providerId }) =>
	(dispatch) => {
		dispatch({
			type: types.SAVE_PROVIDER_TOKEN,
			payload: { token: idToken, provider: providerId }
		});
	};

export const appCheckError = () => (dispatch) => {
	dispatch({
		type: types.AUTH_USER_APP_CHECK_TOKEN_ERROR,
		payload: {
			message: getCodeErrorMessage(API_ERRORS.AUTHENTICATION_FAILED)
		}
	});
};

// ******************** UPDATE USER AGREEMENTS ********************
export const updateUserAgreements = (agreements) => async (dispatch) => {
	try {
		const agreementIds = agreements.map(({ id }) => id);
		const options = { agreement_version_ids: agreementIds };
		await services.post(API.POST_SUBSCRIBER_AGREEMENTS_ACCEPT, options);

		// Get user from local storage
		const user = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER));
		// Set updated user data to local storage with accepted agreements
		localStorage.setItem(
			LOCALSTORAGE_USER,
			JSON.stringify({
				...user,
				status: { ...user.status, agreements },
				isLogged: true,
				acceptedAgreements: true
			})
		);

		// Dispatch an action
		dispatch({
			type: types.AUTH_UPDATE_AGREEMENTS_SUCCESS,
			payload: user
		});
	} catch (error) {
		// Dispatch an error action
		dispatch({
			type: types.AUTH_UPDATE_AGREEMENTS_ERROR,
			payload: error.response.data
		});
	}
};

// ******************** FETCH USER'S PACKETS ********************
export const fetchUserPackets = () => async (dispatch) => {
	try {
		// Get user's packets
		const { data: packets } = await services.get(API.GET_PRODUCTS_PACKETS);
		const { data: products } = await services.get(API.GET_SUBSCRIBER_PRODUCTS_UUIDS);

		const userPackets = checkUserPackets(packets.data, products.data);

		// Dispatch an action
		dispatch({
			type: types.FETCH_USER_PACKETS_SUCCESS,
			payload: { packets: userPackets, products }
		});
	} catch (error) {
		// Dispatch an error action
		dispatch({
			type: types.FETCH_USER_PACKETS_ERROR,
			payload: error.response.status
		});
	}
};

export const fetchUserProductsUuids = () => async (dispatch) => {
	try {
		dispatch({
			type: types.FETCH_USER_PRODUCTS_UUIDS_LOADING,
			payload: true
		});
		// Get user's products
		const { data } = await services.get(API.GET_SUBSCRIBER_PRODUCTS_UUIDS);

		// Dispatch an action
		dispatch({
			type: types.FETCH_USER_PRODUCTS_UUIDS_SUCCESS,
			payload: data?.data
		});
	} catch (error) {
		// Dispatch an error action
		dispatch({
			type: types.FETCH_USER_PRODUCTS_UUIDS_ERROR,
			payload: error.response.status
		});
	}
};

// ******************** FETCH USER'S DETAILS ********************
export const fetchUserDetails = () => async (dispatch) => {
	try {
		// Get user from local storage
		const user = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER));

		// Get login response
		const { data } = await services.get(API.GET_SUBSCRIBER_DETAILS);

		// Create user data
		// Check if hide unavaiable channels
		const hideUnavailable = data.hide_unavailable_products === 1 ? true : false;
		const parentalControlEnabled = data.parental_control_rating ? true : false;
		const userData = {
			...user,
			...data,
			isLogged: true,
			deviceExists: true,
			acceptedAgreements: true,
			parentalControlEnabled,
			hideUnavailable
		};

		// Set user data to local storage
		localStorage.setItem(LOCALSTORAGE_USER, JSON.stringify(userData));

		// Dispatch an action
		dispatch({
			type: types.AUTH_USER_DETAILS_SUCCESS,
			payload: userData
		});
	} catch (error) {
		// Dispatch an error action
		dispatch({
			type: types.AUTH_USER_DETAILS_ERROR,
			payload: error.response
		});
	}
};

// ******************** FETCH USER'S DEVICES ********************
export const fetchUserDevices = () => async (dispatch) => {
	try {
		dispatch({
			type: types.AUTH_FETCH_USER_DEVICES_LOADING,
			payload: true
		});

		// Get user's devices
		const { data } = await services.get(API.GET_SUBSCRIBER_ACTIVE_DEVICES);

		// Dispatch an action
		dispatch({
			type: types.AUTH_FETCH_USER_DEVICES_SUCCESS,
			payload: data.data
		});
	} catch (error) {
		// Dispatch an error action
		dispatch({
			type: types.AUTH_FETCH_USER_DEVICES_ERROR,
			payload: error.response.data
		});
	}
};

// ******************** UPDATE USER'S DEVICE ********************
export const updateUserDevice = (uidOfDeviceToDelete) => async (dispatch) => {
	try {
		// Get user devices
		const {
			data: { token }
		} = await services.put(API.PUT_SUBSCRIBER_DEVICE_TOGGLE, {
			uidOfDeviceToDelete,
			nameOfNewDevice: osName
		});

		// Get user from local storage
		const user = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER));
		// Set updated user data to local storage
		localStorage.setItem(LOCALSTORAGE_USER, JSON.stringify({ ...user, token, isLogged: user.acceptedAgreements }));
		// Dispatch an action
		dispatch({
			type: types.AUTH_UPDATE_USER_DEVICES_SUCCES,
			payload: true
		});
	} catch (error) {
		// Remove user data from local storage
		localStorage.removeItem(LOCALSTORAGE_USER);

		if (error.response) {
			const { codeErrorMessage } = getErrorCodeResults(error);
			// Dispatch an error action
			error.response.data.message = codeErrorMessage;
			dispatch({
				type: types.AUTH_UPDATE_USER_DEVICES_ERROR,
				payload: error.response
			});
		} else {
			// Dispatch an error action
			dispatch({
				type: types.AUTH_UPDATE_USER_DEVICES_ERROR,
				payload: error.response
			});
		}
	}
};
// ******************** UPDATE USER'S DEVICE ********************
export const addUserDevice = () => async (dispatch) => {
	try {
		// Get user devices
		await services.post(API.POST_SUBSCRIBER_DEVICE_NAME, { name: osName });

		// Get user from local storage
		const user = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER));
		// Set updated user data to local storage
		localStorage.setItem(
			LOCALSTORAGE_USER,
			JSON.stringify({
				...user,
				deviceExists: true,
				isLogged: user.acceptedAgreements
			})
		);
		// Dispatch an action
		dispatch({
			type: types.AUTH_UPDATE_USER_DEVICES_SUCCES,
			payload: true
		});
	} catch (error) {
		// Remove user data from local storage
		localStorage.removeItem(LOCALSTORAGE_USER);
		// Dispatch an error action
		dispatch({
			type: types.AUTH_UPDATE_USER_DEVICES_ERROR,
			payload: error.response.data
		});
	}
};

// ******************** SET UNAVAILABLE CHANNEL ********************
export const setUnavailableChannels = (unavailable) => async (dispatch) => {
	try {
		await services.put(API.PUT_SUBSCRIBER_HIDE_UNAVAILABLE, {
			hide: unavailable
		});
		// Get user from local storage
		const user = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER));
		const updatedUser = { ...user, hideUnavailable: unavailable };
		// Set updated user data to local storage
		localStorage.setItem(LOCALSTORAGE_USER, JSON.stringify({ ...updatedUser }));
		// Dispatch an action
		dispatch({
			type: types.SET_USER_UNAVAILABLE_CHANNELS_SUCCESS,
			payload: unavailable
		});
	} catch (error) {
		// Dispatch an error action
		dispatch({
			type: types.SET_USER_UNAVAILABLE_CHANNELS_ERROR,
			payload: true
		});
	}
};

// ******************** SET SUBSCRIBER IN LOCAL ********************
export const setSubscriberInLocal = () => async (dispatch, getState) => {
	try {
		// Get user location
		const {
			data: { value: isUserInLocal }
		} = await services.get(API.GET_SUBSCRIBER_IN_LOCAL);

		// Get user from local storage
		const user = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER));
		const updatedUser = { ...user, subscriber_local_active: isUserInLocal };
		// Set updated user data to local storage
		localStorage.setItem(LOCALSTORAGE_USER, JSON.stringify({ ...updatedUser }));

		isUserInLocal && setChannelsByLocal()(dispatch, getState);

		// Dispatch an action
		dispatch({
			type: types.SET_USER_IN_LOCAL,
			payload: isUserInLocal
		});
	} catch (error) {
		//
	}
};

// ******************** CLEAR AUTHORIZATION ********************
export const clearUserAuthorize = () => (dispatch) => {
	localStorage.removeItem(LOCALSTORAGE_USER);

	dispatch({
		type: types.AUTH_USER_CLEAR,
		payload: null
	});
};

// ******************** SIGN OUT ********************
export const signOut =
	(authenticate = true) =>
	async (dispatch) => {
		try {
			// Dispatch an loading action
			dispatch({
				type: types.SIGN_OUT_LOADING,
				payload: true
			});

			if (authenticate) {
				// Sign out user from application
				await services.post(API.POST_SUBSCRIBER_LOGOUT);
			}

			// Remove user data from local storage
			localStorage.removeItem(LOCALSTORAGE_USER);

			// Dispatch an action
			dispatch({
				type: types.SIGN_OUT,
				payload: true
			});
		} catch (error) {
			// Dispatch an error action
			dispatch({
				type: types.SIGN_OUT_ERROR,
				payload: true
			});
		}
	};

export const deleteProviderToken = () => (dispatch) => {
	// Dispatch an action
	dispatch({
		type: types.DELETE_PROVIDER_TOKEN
	});
};
